import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Plus,
  Calendar,
  Users,
  Wallet,
  Sparkles,
  AlertTriangle,
  Search,
  X,
  ChevronRight,
  Upload,
  FileText,
  Wand2,
  Loader2,
  Bell,
  Mail,
  CheckCircle2,
  Clock,
  Send,
  Image as ImageIcon,
  TrendingUp,
} from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

type CampaignType = 'pret_taiat' | 'voucher'

interface MkCampaign {
  id: number
  b4lId: string
  name: string
  step: number
  type: CampaignType
  startDate: string | null
  endDate: string | null
  budget: number | null
  tagline: string | null
  briefFileUrl: string | null
  briefContent: string | null
  voucherCode: string | null
  voucherPct: number | null
  bannersDone: number
  webBlastConfirmed: boolean
  doAdsConfirmed: boolean
  dayProgress: number | null
  dayTotal: number | null
  reportReceived: boolean
  reportData: Record<string, number> | null
  bannersRemoved: boolean
  finalized: boolean
  marketingMessages: Record<string, unknown> | null
  marketingSent: Record<string, boolean> | null
  sentEmails: Record<
    string,
    { sentAt: string; sentByUserId: number | null; b4lId: string; to: string; subject: string }
  >

  createdAt: string
  updatedAt: string
  products: Array<{
    id: number
    productId: number
    discountPct: number
    product: { id: number; sku: string; name: string; brand?: string }
  }>
  banners: Array<{
    id: number
    slug: string
    size: string
    target: string
    status: string
    fileUrl: string | null
  }>
  createdBy: { id: number; name: string }
}

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  active: boolean
}

interface Conflict {
  productId: number
  sku: string
  name: string
  conflictWith: {
    campaignId: number
    campaignName: string
    b4lId: string
    startDate: string | null
    endDate: string | null
  }
  daysDiff: number
}

const STEP_NAMES = ['Setup', 'Brief', 'Producție', 'Lansare', 'Live', 'Închidere']

const COLUMNS: Array<{ key: string; label: string; match: (c: MkCampaign) => boolean }> = [
  { key: 'setup', label: 'Setup', match: (c) => c.step <= 2 && !c.finalized },
  { key: 'productie', label: 'Producție', match: (c) => c.step === 3 && !c.finalized },
  {
    key: 'live',
    label: 'Lansare / Live',
    match: (c) => (c.step === 4 || c.step === 5) && !c.finalized,
  },
  { key: 'inchise', label: 'Încheiate', match: (c) => c.step === 6 || c.finalized },
]

function formatDate(s: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  })
}

function formatPeriod(start: string | null, end: string | null): string {
  if (!start && !end) return '—'
  return `${formatDate(start)} – ${formatDate(end)}`
}

function getTiers(campaign: MkCampaign): Array<{ pct: number; products: string[] }> | null {
  if (campaign.products.length === 0) return null
  const byPct = new Map<number, string[]>()
  for (const p of campaign.products) {
    const pct = Number(p.discountPct)
    const list = byPct.get(pct) ?? []
    list.push(p.product.name)
    byPct.set(pct, list)
  }
  const tiers = [...byPct.entries()].map(([pct, products]) => ({ pct, products }))
  tiers.sort((a, b) => b.pct - a.pct)
  return tiers
}

function getDayProgress(campaign: MkCampaign): { day: number; total: number } | null {
  if (!campaign.startDate || !campaign.endDate) return null
  const now = Date.now()
  const start = new Date(campaign.startDate).getTime()
  const end = new Date(campaign.endDate).getTime()
  if (now < start || now > end) return null
  const day = Math.floor((now - start) / 86_400_000) + 1
  const total = Math.floor((end - start) / 86_400_000) + 1
  return { day, total }
}

interface CampaignNotification {
  campaignId: number
  campaignName: string
  icon: 'banner' | 'launch' | 'marketing' | 'reply' | 'closing'
  title: string
  desc: string
  cta: string
}

function computeNotifications(campaigns: MkCampaign[]): CampaignNotification[] {
  const notifs: CampaignNotification[] = []
  for (const c of campaigns) {
    if (c.finalized) continue

    if (c.step === 3 && c.bannersDone === 9) {
      notifs.push({
        campaignId: c.id,
        campaignName: c.name,
        icon: 'launch',
        title: `${c.name} · 9/9 bannere gata`,
        desc: 'Pregătit pentru lansare — trimite emailurile WebBlast + DoAds.',
        cta: 'Vezi campania →',
      })
    }

    if (c.step === 4 && (!c.webBlastConfirmed || !c.doAdsConfirmed)) {
      const waiting: string[] = []
      if (!c.webBlastConfirmed) waiting.push('WebBlast')
      if (!c.doAdsConfirmed) waiting.push('DoAds')
      notifs.push({
        campaignId: c.id,
        campaignName: c.name,
        icon: 'reply',
        title: `${c.name} · se așteaptă răspunsul`,
        desc: `Se așteaptă confirmarea de la: ${waiting.join(' + ')}.`,
        cta: 'Vezi emailurile →',
      })
    }

    const dp = getDayProgress(c)
    if (c.step === 5 && dp) {
      const ms = (c.marketingSent ?? {}) as Record<string, unknown>
      if (dp.day === 2) {
        const due: string[] = []
        if (!ms.email1) due.push('Email 1')
        if (!ms.sms) due.push('SMS')
        if (!ms.whatsapp) due.push('WhatsApp')
        if (due.length > 0) {
          notifs.push({
            campaignId: c.id,
            campaignName: c.name,
            icon: 'marketing',
            title: `${c.name} · ziua 2 (azi)`,
            desc: `Trimite în Omnisend / Chatarmin: ${due.join(' + ')}.`,
            cta: 'Vezi mesajele →',
          })
        }
      }
      if (dp.day === dp.total - 1 && !ms.email2) {
        notifs.push({
          campaignId: c.id,
          campaignName: c.name,
          icon: 'marketing',
          title: `${c.name} · penultima zi`,
          desc: 'Trimite Email 2 (Last call) în Omnisend.',
          cta: 'Vezi mesajul →',
        })
      }
    }

    if (c.step === 6 && (!c.reportReceived || !c.bannersRemoved)) {
      const waiting: string[] = []
      if (!c.reportReceived) waiting.push('raport DoAds')
      if (!c.bannersRemoved) waiting.push('confirmare WebBlast')
      notifs.push({
        campaignId: c.id,
        campaignName: c.name,
        icon: 'closing',
        title: `${c.name} · închidere în curs`,
        desc: `Se așteaptă: ${waiting.join(' + ')}. Finalizare automată după ambele.`,
        cta: 'Vezi emailurile →',
      })
    }
  }
  return notifs
}

export function CampaniiMarketingPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<number | null>(null)
  const canWrite = useAuthStore((s) => s.permissions.includes('marketing.campaigns_mk:write'))

  const listQuery = useQuery<{ campaigns: MkCampaign[] }>({
    queryKey: ['mk-campaigns'],
    queryFn: async () => (await api.get('/marketing/campaigns-mk')).data,
    // Pagina colaborativa — Victor + Alex creeaza campanii in paralel.
    // Polling 30s + refresh la revenire pe tab ca sa nu trebuiasca F5.
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  })

  const campaigns = listQuery.data?.campaigns ?? []

  const kpi = useMemo(() => {
    const live = campaigns.filter((c) => c.step === 5 && !c.finalized).length
    const finalized = campaigns.filter((c) => c.finalized)
    const totalBudget = finalized.reduce((sum, c) => sum + (c.budget ?? 0), 0)
    const briefActive = campaigns.filter((c) => c.step >= 2 && c.step <= 3 && !c.finalized).length
    return {
      live,
      finalized: finalized.length,
      totalBudget,
      briefActive,
    }
  }, [campaigns])

  const notifications = useMemo(() => computeNotifications(campaigns), [campaigns])

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-b4l-text">Campanii marketing</h1>
          <p className="text-sm text-b4l-text-muted">
            Campanii online B2C — operate de Marketing cu agenții externe (360 / WebBlast / DoAds)
          </p>
        </div>
        {canWrite && (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Campanie nouă
          </Button>
        )}
      </div>

      {notifications.length > 0 && (
        <NotificationsPanel
          notifications={notifications}
          onSelectCampaign={(id) => setDetailId(id)}
        />
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <KpiCard
          icon={Sparkles}
          label="Live acum"
          value={String(kpi.live)}
          accent="text-emerald-700"
        />
        <KpiCard
          icon={Calendar}
          label="Brief activ (Pas 2-3)"
          value={String(kpi.briefActive)}
          accent="text-amber-700"
        />
        <KpiCard
          icon={Users}
          label="Campanii finalizate"
          value={String(kpi.finalized)}
          accent="text-b4l-green"
        />
        <KpiCard
          icon={Wallet}
          label="Buget cumulat (finalizate)"
          value={`${kpi.totalBudget.toFixed(0)} EUR`}
          accent="text-b4l-text"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {COLUMNS.map((col) => (
          <PipelineColumn
            key={col.key}
            label={col.label}
            campaigns={campaigns.filter(col.match)}
            onSelect={(id) => setDetailId(id)}
          />
        ))}
      </div>

      {createOpen && <CreateCampaignModal onClose={() => setCreateOpen(false)} />}
      {detailId !== null && <DetailCampaignModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  )
}

function KpiCard({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Sparkles
  label: string
  value: string
  accent: string
}) {
  return (
    <div className="rounded-lg border border-b4l-border bg-white p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`rounded-md bg-b4l-bg-soft p-2 ${accent}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-b4l-text-muted">{label}</div>
          <div className="text-2xl font-semibold text-b4l-text">{value}</div>
        </div>
      </div>
    </div>
  )
}

function NotificationsPanel({
  notifications,
  onSelectCampaign,
}: {
  notifications: CampaignNotification[]
  onSelectCampaign: (id: number) => void
}) {
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white">
          <Bell className="h-3.5 w-3.5" />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
          AI a observat — {notifications.length} acțiun{notifications.length === 1 ? 'e' : 'i'} în
          așteptare
        </span>
      </div>
      <div className="space-y-1.5">
        {notifications.map((n, idx) => {
          const Icon =
            n.icon === 'banner'
              ? ImageIcon
              : n.icon === 'launch'
                ? Send
                : n.icon === 'marketing'
                  ? Mail
                  : n.icon === 'reply'
                    ? Clock
                    : CheckCircle2
          return (
            <button
              key={`${n.campaignId}-${idx}`}
              type="button"
              onClick={() => onSelectCampaign(n.campaignId)}
              className="grid w-full grid-cols-[28px_1fr_auto] items-center gap-3 rounded border border-b4l-border bg-white p-2.5 text-left text-xs transition hover:border-amber-400 hover:bg-amber-50"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <Icon className="h-3.5 w-3.5" />
              </div>
              <div>
                <div className="font-semibold text-b4l-text">{n.title}</div>
                <div className="mt-0.5 text-[11px] text-b4l-text-muted">{n.desc}</div>
              </div>
              <span className="whitespace-nowrap text-[11px] font-semibold text-amber-700">
                {n.cta}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function PipelineColumn({
  label,
  campaigns,
  onSelect,
}: {
  label: string
  campaigns: MkCampaign[]
  onSelect: (id: number) => void
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between border-b border-b4l-border pb-2">
        <h2 className="text-sm font-semibold text-b4l-text">{label}</h2>
        <span className="text-xs text-b4l-text-muted">{campaigns.length}</span>
      </div>
      <div className="space-y-2">
        {campaigns.length === 0 && (
          <p className="text-xs italic text-b4l-text-muted">Nicio campanie</p>
        )}
        {campaigns.map((c) => (
          <CampaignCard key={c.id} campaign={c} onClick={() => onSelect(c.id)} />
        ))}
      </div>
    </div>
  )
}

function CampaignCard({ campaign, onClick }: { campaign: MkCampaign; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full cursor-pointer rounded-lg border border-b4l-border bg-white p-3 text-left shadow-sm transition hover:bg-b4l-bg"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="font-medium text-b4l-text">{campaign.name}</div>
          <div className="mt-0.5 font-mono text-[10px] text-b4l-text-muted">{campaign.b4lId}</div>
        </div>
        <span
          className={`rounded px-2 py-0.5 text-[10px] font-medium ${
            campaign.type === 'voucher'
              ? 'bg-purple-100 text-purple-700'
              : 'bg-amber-100 text-amber-700'
          }`}
        >
          {campaign.type === 'voucher' ? 'Voucher' : 'Preț tăiat'}
        </span>
      </div>
      <div className="mt-2 text-xs text-b4l-text-muted">
        {formatPeriod(campaign.startDate, campaign.endDate)}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        {STEP_NAMES.map((stepLabel, idx) => (
          <div
            key={stepLabel}
            title={stepLabel}
            className={`h-1.5 flex-1 rounded-full ${
              idx + 1 <= campaign.step ? 'bg-b4l-green' : 'bg-b4l-border'
            }`}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] text-b4l-text-muted">
        <span>
          Pas {campaign.step}/6 — {STEP_NAMES[campaign.step - 1]}
        </span>
        <span>{campaign.bannersDone}/9 bannere</span>
      </div>
    </button>
  )
}

// ============================================================================
// CREATE MODAL with product selector + 30-day validation
// ============================================================================

interface SelectedProduct {
  productId: number
  sku: string
  name: string
  discountPct: number
}

function CreateCampaignModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [type, setType] = useState<CampaignType>('pret_taiat')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [budget, setBudget] = useState('')
  const [voucherCode, setVoucherCode] = useState('')
  const [voucherPct, setVoucherPct] = useState('')
  const [tagline, setTagline] = useState('')
  const [selected, setSelected] = useState<SelectedProduct[]>([])
  const [conflicts, setConflicts] = useState<Conflict[]>([])

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products', 'active'],
    queryFn: async () => (await api.get('/products')).data,
  })
  const allProducts = productsQuery.data?.products?.filter((p) => p.active) ?? []

  const productIds = useMemo(() => selected.map((s) => s.productId), [selected])

  // Validare 30z live (debounced)
  useEffect(() => {
    if (type !== 'pret_taiat' || productIds.length === 0 || !startDate || !endDate) {
      setConflicts([])
      return
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post('/marketing/campaigns-mk/check-30z', {
          productIds,
          startDate: new Date(startDate).toISOString(),
          endDate: new Date(endDate).toISOString(),
        })
        setConflicts(data.conflicts)
      } catch {
        setConflicts([])
      }
    }, 350)
    return () => clearTimeout(timer)
  }, [productIds, startDate, endDate, type])

  const conflictByProductId = useMemo(() => {
    const m = new Map<number, Conflict>()
    for (const c of conflicts) m.set(c.productId, c)
    return m
  }, [conflicts])

  const createMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { name, type }
      if (startDate) body.startDate = new Date(startDate).toISOString()
      if (endDate) body.endDate = new Date(endDate).toISOString()
      if (budget) body.budget = Number(budget)
      if (tagline) body.tagline = tagline
      if (type === 'voucher') {
        body.voucherCode = voucherCode
        body.voucherPct = Number(voucherPct)
      }
      body.products = selected.map((s) => ({ productId: s.productId, discountPct: s.discountPct }))
      return (await api.post('/marketing/campaigns-mk', body)).data
    },
    onSuccess: () => {
      toast.success('Campanie creată')
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      onClose()
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Eroare la creare'
      toast.error(message)
    },
  })

  const canSubmit =
    name.length > 0 &&
    !createMutation.isPending &&
    conflicts.length === 0 &&
    (type === 'pret_taiat' || (voucherCode && voucherPct))

  return (
    <Modal
      open
      onClose={onClose}
      title="Campanie nouă"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => createMutation.mutate()} disabled={!canSubmit}>
            {createMutation.isPending ? 'Se creează...' : 'Creează'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <FormField label="Nume campanie">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex: Easter Balance Days"
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
          />
        </FormField>

        <FormField label="Tip discount">
          <div className="flex gap-2">
            <label className="flex flex-1 cursor-pointer items-center gap-2 rounded border border-b4l-border bg-white px-3 py-2 text-sm">
              <input
                type="radio"
                checked={type === 'pret_taiat'}
                onChange={() => setType('pret_taiat')}
              />
              Preț tăiat (per produs)
            </label>
            <label className="flex flex-1 cursor-pointer items-center gap-2 rounded border border-b4l-border bg-white px-3 py-2 text-sm">
              <input
                type="radio"
                checked={type === 'voucher'}
                onChange={() => setType('voucher')}
              />
              Voucher (cod uniform)
            </label>
          </div>
        </FormField>

        {type === 'voucher' && (
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Cod voucher">
              <input
                type="text"
                value={voucherCode}
                onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                placeholder="MAY15"
                className="w-full rounded border border-b4l-border px-3 py-2 font-mono text-sm outline-none focus:border-b4l-green"
              />
            </FormField>
            <FormField label="Discount uniform %">
              <input
                type="number"
                value={voucherPct}
                onChange={(e) => setVoucherPct(e.target.value)}
                placeholder="15"
                min={0}
                max={100}
                className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
              />
            </FormField>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <FormField label="Start">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
          </FormField>
          <FormField label="Sfârșit">
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
          </FormField>
        </div>

        <FormField label="Buget DoAds (EUR)">
          <input
            type="number"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="500"
            min={0}
            step={0.01}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
          />
        </FormField>

        <FormField label="Tagline (opțional — AI îl poate genera mai târziu, din brief)">
          <input
            type="text"
            value={tagline}
            onChange={(e) => setTagline(e.target.value)}
            placeholder="ex: Echilibru pentru sărbători"
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
          />
        </FormField>

        <ProductSelector
          allProducts={allProducts}
          selected={selected}
          setSelected={setSelected}
          conflictByProductId={conflictByProductId}
          type={type}
          uniformDiscount={type === 'voucher' ? Number(voucherPct) || 0 : null}
        />

        {conflicts.length > 0 && (
          <div className="rounded border border-b4l-red bg-b4l-red-light p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-b4l-red-dark" />
              <div className="flex-1 space-y-1.5">
                <div className="text-sm font-semibold text-b4l-red-dark">
                  Conflict OUG 84/2022 (regula 30 zile)
                </div>
                <p className="text-xs text-b4l-red-dark">
                  {conflicts.length} produs(e) au fost folosite la preț tăiat în
                  ultimele/următoarele 30 zile. Trebuie să elimini produsul sau să schimbi
                  perioada/tipul.
                </p>
                <ul className="space-y-1 text-[11px] text-b4l-red-dark">
                  {conflicts.slice(0, 5).map((c, idx) => (
                    <li key={`${c.productId}-${idx}`}>
                      • <strong>{c.sku}</strong> — în {c.conflictWith.campaignName} (
                      {c.conflictWith.b4lId},{' '}
                      {formatPeriod(c.conflictWith.startDate, c.conflictWith.endDate)})
                    </li>
                  ))}
                  {conflicts.length > 5 && (
                    <li className="italic">... +{conflicts.length - 5} alt(e)</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}

function ProductSelector({
  allProducts,
  selected,
  setSelected,
  conflictByProductId,
  type,
  uniformDiscount,
}: {
  allProducts: Product[]
  selected: SelectedProduct[]
  setSelected: (s: SelectedProduct[]) => void
  conflictByProductId: Map<number, Conflict>
  type: CampaignType
  uniformDiscount: number | null
}) {
  const [search, setSearch] = useState('')
  const selectedIds = useMemo(() => new Set(selected.map((s) => s.productId)), [selected])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return [] as Product[]
    return allProducts
      .filter(
        (p) =>
          !selectedIds.has(p.id) &&
          (p.sku.toLowerCase().includes(q) ||
            p.name.toLowerCase().includes(q) ||
            p.brand.toLowerCase().includes(q)),
      )
      .slice(0, 12)
  }, [allProducts, search, selectedIds])

  const add = (p: Product) => {
    setSelected([
      ...selected,
      {
        productId: p.id,
        sku: p.sku,
        name: p.name,
        discountPct: type === 'voucher' ? (uniformDiscount ?? 0) : 10,
      },
    ])
    setSearch('')
  }

  const remove = (productId: number) => {
    setSelected(selected.filter((s) => s.productId !== productId))
  }

  const updateDiscount = (productId: number, pct: number) => {
    setSelected(selected.map((s) => (s.productId === productId ? { ...s, discountPct: pct } : s)))
  }

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
        Produse {type === 'pret_taiat' ? '(cu discount per produs)' : '(toate cu discount uniform)'}
      </label>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-b4l-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Caută produs după SKU, nume sau brand..."
          className="w-full rounded border border-b4l-border py-2 pl-9 pr-3 text-sm outline-none focus:border-b4l-green"
        />
        {filtered.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded border border-b4l-border bg-white shadow-lg">
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => add(p)}
                className="block w-full px-3 py-2 text-left text-sm hover:bg-b4l-bg"
              >
                <div className="font-mono text-xs text-b4l-text-muted">{p.sku}</div>
                <div className="text-b4l-text">{p.name}</div>
                <div className="text-[10px] text-b4l-text-dim">{p.brand}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {selected.map((s) => {
            const conflict = conflictByProductId.get(s.productId)
            return (
              <div
                key={s.productId}
                className={`flex items-center gap-2 rounded border p-2 text-sm ${
                  conflict ? 'border-b4l-red bg-b4l-red-light' : 'border-b4l-border bg-white'
                }`}
              >
                <div className="flex-1">
                  <div className="font-mono text-[10px] text-b4l-text-muted">{s.sku}</div>
                  <div className="text-b4l-text">{s.name}</div>
                  {conflict && (
                    <div className="mt-0.5 text-[10px] text-b4l-red-dark">
                      ⚠ Conflict cu {conflict.conflictWith.campaignName} (
                      {conflict.conflictWith.b4lId})
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    value={s.discountPct}
                    onChange={(e) => updateDiscount(s.productId, Number(e.target.value))}
                    disabled={type === 'voucher'}
                    min={0}
                    max={100}
                    className="w-16 rounded border border-b4l-border px-2 py-1 text-right text-xs outline-none focus:border-b4l-green disabled:bg-b4l-bg disabled:text-b4l-text-muted"
                  />
                  <span className="text-xs text-b4l-text-muted">%</span>
                </div>
                <button
                  type="button"
                  onClick={() => remove(s.productId)}
                  className="rounded p-1 text-b4l-text-muted hover:bg-b4l-bg hover:text-b4l-red"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// DETAIL MODAL — contextual per-step view (aligned cu prototipul HTML)
// ============================================================================

function DetailCampaignModal({ id, onClose }: { id: number; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [viewingStep, setViewingStep] = useState<number | null>(null)
  const [briefModalOpen, setBriefModalOpen] = useState(false)
  const [emailModalType, setEmailModalType] = useState<EmailType | null>(null)
  const [marketingModalOpen, setMarketingModalOpen] = useState(false)

  const detailQuery = useQuery<{ campaign: MkCampaign }>({
    queryKey: ['mk-campaigns', id],
    queryFn: async () => (await api.get(`/marketing/campaigns-mk/${id}`)).data,
  })

  const advanceMutation = useMutation({
    mutationFn: async (newStep: number) => {
      return (await api.patch(`/marketing/campaigns-mk/${id}`, { step: newStep })).data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', id] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => api.delete(`/marketing/campaigns-mk/${id}`),
    onSuccess: () => {
      toast.success('Campanie ștearsă')
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      onClose()
    },
  })

  if (detailQuery.isLoading || !detailQuery.data) {
    return (
      <Modal open onClose={onClose} title="Se încarcă..." size="xl">
        <p className="text-sm text-b4l-text-muted">Se încarcă detaliile...</p>
      </Modal>
    )
  }

  const c = detailQuery.data.campaign
  const viewed = viewingStep ?? c.step
  const isViewingPast = viewed !== c.step

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title={`${c.name} — ${c.b4lId}`}
        size="xl"
        footer={
          <DetailFooter
            campaign={c}
            viewedStep={viewed}
            isViewingPast={isViewingPast}
            onBackToCurrent={() => setViewingStep(null)}
            onPrepareEmail={(emailType) => setEmailModalType(emailType)}
            onSkipToStep={(step) => advanceMutation.mutate(step)}
            onDelete={() => deleteMutation.mutate()}
            isAdvancing={advanceMutation.isPending}
          />
        }
      >
        <div className="space-y-4">
          {/* Timeline clickable */}
          <Timeline
            currentStep={c.step}
            viewedStep={viewed}
            onSelectStep={(step) => setViewingStep(step === c.step ? null : step)}
          />

          {isViewingPast && (
            <div className="rounded border border-blue-300 bg-blue-50 p-2 text-xs text-blue-900">
              📜 Arhivă pasul {viewed} — campania este la pasul {c.step}.
            </div>
          )}

          <StepView
            campaign={c}
            step={viewed}
            isInteractive={!isViewingPast}
            onOpenBriefModal={() => setBriefModalOpen(true)}
            onOpenMarketingModal={() => setMarketingModalOpen(true)}
            onOpenEmailModal={(emailType) => setEmailModalType(emailType)}
          />
        </div>
      </Modal>

      {briefModalOpen && <BriefAiModal campaign={c} onClose={() => setBriefModalOpen(false)} />}

      {marketingModalOpen && (
        <Modal
          open
          onClose={() => setMarketingModalOpen(false)}
          title="Marketing copy — 4 mesaje"
          size="xl"
        >
          <MarketingTab campaign={c} />
        </Modal>
      )}

      {emailModalType && (
        <EmailPreviewModal
          campaignId={c.id}
          emailType={emailModalType}
          emailLabel={CAMPAIGN_EMAILS.find((e) => e.type === emailModalType)?.label ?? ''}
          onClose={() => setEmailModalType(null)}
        />
      )}
    </>
  )
}

function Timeline({
  currentStep,
  viewedStep,
  onSelectStep,
}: {
  currentStep: number
  viewedStep: number
  onSelectStep: (step: number) => void
}) {
  return (
    <div className="flex items-center gap-2">
      {STEP_NAMES.map((label, idx) => {
        const stepNum = idx + 1
        const isPast = stepNum < currentStep
        const isCurrent = stepNum === currentStep
        const isViewing = stepNum === viewedStep && stepNum !== currentStep
        const isClickable = stepNum <= currentStep
        return (
          <div key={label} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => isClickable && onSelectStep(stepNum)}
              disabled={!isClickable}
              className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold transition ${
                isPast
                  ? 'bg-b4l-green text-white hover:scale-110'
                  : isCurrent
                    ? 'bg-b4l-green-light text-b4l-green-dark ring-2 ring-b4l-green'
                    : 'bg-b4l-bg text-b4l-text-muted'
              } ${isViewing ? 'outline outline-2 outline-blue-500 outline-offset-2' : ''} ${
                isClickable ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              {stepNum}
            </button>
            <span
              className={`text-xs ${isCurrent ? 'font-semibold text-b4l-text' : 'text-b4l-text-muted'}`}
            >
              {label}
            </span>
            {idx < STEP_NAMES.length - 1 && <ChevronRight className="h-3 w-3 text-b4l-text-dim" />}
          </div>
        )
      })}
    </div>
  )
}

function DetailFooter({
  campaign,
  viewedStep,
  isViewingPast,
  onBackToCurrent,
  onPrepareEmail,
  onSkipToStep,
  onDelete,
  isAdvancing,
}: {
  campaign: MkCampaign
  viewedStep: number
  isViewingPast: boolean
  onBackToCurrent: () => void
  onPrepareEmail: (emailType: EmailType) => void
  onSkipToStep: (step: number) => void
  onDelete: () => void
  isAdvancing: boolean
}) {
  if (isViewingPast) {
    return (
      <div className="flex w-full items-center justify-between gap-2">
        <span className="text-xs text-b4l-text-muted">Pasul {viewedStep} · executat anterior</span>
        <Button variant="secondary" onClick={onBackToCurrent}>
          ↩ Înapoi la curent
        </Button>
      </div>
    )
  }

  const step = campaign.step
  const sentBrief360 = campaign.sentEmails?.brief_360
  const sentLansareWb = campaign.sentEmails?.lansare_webblast
  const sentLansareDa = campaign.sentEmails?.lansare_doads
  const bannersDone = campaign.bannersDone

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <Button
        variant="danger"
        onClick={() => {
          if (confirm(`Ștergi campania "${campaign.name}"?`)) onDelete()
        }}
        disabled={isAdvancing}
      >
        Șterge
      </Button>
      <div className="flex items-center gap-2">
        {step === 1 && (
          <>
            <span className="hidden text-xs text-b4l-text-muted md:block">
              {campaign.briefContent
                ? 'Brief atașat — pregătește emailul către 360'
                : 'Atașează un brief înainte de Pas 2'}
            </span>
            <Button
              onClick={() => onPrepareEmail('brief_360')}
              disabled={!campaign.briefContent || !!sentBrief360}
            >
              {sentBrief360 ? '✓ Brief trimis' : 'Pregătește email →'}
            </Button>
          </>
        )}
        {step === 2 && (
          <span className="text-xs text-b4l-text-muted">
            Brief trimis · se așteaptă bannerele de la 360
          </span>
        )}
        {step === 3 && (
          <>
            <span className="hidden text-xs text-b4l-text-muted md:block">
              {bannersDone === 9 ? '9/9 ✓ pregătit pentru Lansare' : `${bannersDone}/9 bannere`}
            </span>
            <Button
              onClick={() => onPrepareEmail('lansare_webblast')}
              disabled={bannersDone < 9 || !!sentLansareWb}
              variant={sentLansareWb ? 'secondary' : 'primary'}
            >
              {sentLansareWb ? '✓ WebBlast' : 'Email WebBlast →'}
            </Button>
            <Button
              onClick={() => onPrepareEmail('lansare_doads')}
              disabled={bannersDone < 9 || !!sentLansareDa}
              variant={sentLansareDa ? 'secondary' : 'primary'}
            >
              {sentLansareDa ? '✓ DoAds' : 'Email DoAds →'}
            </Button>
          </>
        )}
        {step === 4 && (
          <span className="text-xs text-b4l-text-muted">
            {campaign.webBlastConfirmed && campaign.doAdsConfirmed
              ? 'Ambele confirmate · campanie pornește automat'
              : 'AI așteaptă răspunsurile email'}
          </span>
        )}
        {step === 5 && (
          <Button variant="secondary" onClick={() => onSkipToStep(6)} disabled={isAdvancing}>
            ⏭ Sari la încheiere
          </Button>
        )}
        {step === 6 && (
          <>
            <Button
              onClick={() => onPrepareEmail('cerere_raport_doads')}
              disabled={!!campaign.sentEmails?.cerere_raport_doads || campaign.reportReceived}
              variant={campaign.sentEmails?.cerere_raport_doads ? 'secondary' : 'primary'}
            >
              {campaign.sentEmails?.cerere_raport_doads ? '✓ Raport cerut' : 'Cere raport DoAds'}
            </Button>
            <Button
              onClick={() => onPrepareEmail('cerere_scoatere_webblast')}
              disabled={!!campaign.sentEmails?.cerere_scoatere_webblast || campaign.bannersRemoved}
              variant={campaign.sentEmails?.cerere_scoatere_webblast ? 'secondary' : 'primary'}
            >
              {campaign.sentEmails?.cerere_scoatere_webblast ? '✓ Cerut' : 'Scoatere bannere'}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// CONTEXTUAL STEP VIEW — replaces tabs (aligned cu prototipul HTML)
// ============================================================================

function StepView({
  campaign,
  step,
  isInteractive,
  onOpenBriefModal,
  onOpenMarketingModal,
  onOpenEmailModal,
}: {
  campaign: MkCampaign
  step: number
  isInteractive: boolean
  onOpenBriefModal: () => void
  onOpenMarketingModal: () => void
  onOpenEmailModal: (emailType: EmailType) => void
}) {
  if (step === 1) return <SetupView campaign={campaign} onOpenBriefModal={onOpenBriefModal} />
  if (step === 2)
    return (
      <BriefSentView
        campaign={campaign}
        onOpenBriefModal={onOpenBriefModal}
        onOpenEmailModal={onOpenEmailModal}
      />
    )
  if (step === 3)
    return (
      <ProductionView
        campaign={campaign}
        isInteractive={isInteractive}
        onOpenMarketingModal={onOpenMarketingModal}
      />
    )
  if (step === 4)
    return (
      <LansareView
        campaign={campaign}
        onOpenMarketingModal={onOpenMarketingModal}
        onOpenEmailModal={onOpenEmailModal}
      />
    )
  if (step === 5)
    return <LiveView campaign={campaign} onOpenMarketingModal={onOpenMarketingModal} />
  if (step === 6) return <ClosedView campaign={campaign} />
  return null
}

function SetupView({
  campaign,
  onOpenBriefModal,
}: {
  campaign: MkCampaign
  onOpenBriefModal: () => void
}) {
  const tiers = getTiers(campaign)
  const briefExcerpt = campaign.briefContent
    ? campaign.briefContent
        .split('\n')
        .filter((l) => l.trim() && !/^[A-ZĂÂÎȘȚ\s&]+$/.test(l))
        .slice(0, 2)
        .join(' ')
        .substring(0, 220)
    : ''

  return (
    <div className="space-y-4 rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-b4l-text">Setup campanie</h4>
        <span className="rounded bg-b4l-green text-white text-[10px] font-semibold px-2 py-0.5">
          {campaign.step === 1 ? 'Activ' : 'Completat'}
        </span>
      </div>

      {campaign.briefContent ? (
        <button
          type="button"
          onClick={onOpenBriefModal}
          className="block w-full rounded-lg border-2 border-b4l-green bg-white p-4 text-left transition hover:bg-b4l-green-light"
        >
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded bg-b4l-green text-white">
              <FileText className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-b4l-text">
                Brief: {campaign.briefFileUrl ? campaign.briefFileUrl.split('/').pop() : 'atașat'}
              </div>
              <div className="text-[11px] text-b4l-text-muted">
                Date extrase de AI · apasă pentru a vedea și edita
              </div>
            </div>
          </div>
          {briefExcerpt && (
            <div className="rounded border-l-4 border-b4l-green bg-b4l-bg-soft px-3 py-2 text-xs italic text-b4l-text-muted">
              {briefExcerpt}…
            </div>
          )}
          <div className="mt-2 text-[11px] font-semibold text-b4l-green-dark">
            📖 Deschide brief complet →
          </div>
        </button>
      ) : (
        <button
          type="button"
          onClick={onOpenBriefModal}
          className="block w-full rounded-lg border-2 border-dashed border-b4l-border bg-white p-4 text-left transition hover:border-b4l-green hover:bg-b4l-green-light"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded bg-b4l-bg text-b4l-text-muted">
              <Upload className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-b4l-text">
                Atașează un brief sau generează cu AI
              </div>
              <div className="text-[11px] text-b4l-text-muted">
                Încarcă un .docx (AI extrage datele) sau descrie campania și AI generează brieful.
              </div>
            </div>
          </div>
        </button>
      )}

      {campaign.tagline && (
        <div className="rounded border border-b4l-border bg-white px-3 py-2 text-sm italic text-b4l-text-muted">
          &laquo;{campaign.tagline}&raquo;
        </div>
      )}

      {tiers && tiers.length > 0 && (
        <div>
          <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
            Reduceri per produs · {campaign.products.length} SKU
          </h5>
          <div className="space-y-1.5">
            {tiers.map((t) => (
              <div key={t.pct} className="rounded border border-b4l-border bg-white p-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-b4l-green-dark">−{t.pct}%</span>
                  <span className="text-[11px] text-b4l-text-muted">
                    {t.products.length} produs{t.products.length !== 1 ? 'e' : ''}
                  </span>
                </div>
                <div className="mt-1 text-[11px] text-b4l-text">
                  {t.products.slice(0, 5).join(' · ')}
                  {t.products.length > 5 && ` · +${t.products.length - 5} alt(e)`}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Detalii
        </h5>
        <div className="space-y-1.5 rounded border border-b4l-border bg-white p-3 text-xs">
          <Row label="Tip">
            {campaign.type === 'pret_taiat'
              ? 'Preț tăiat (auto-aplicat)'
              : 'Voucher (cod la checkout)'}
          </Row>
          {campaign.type === 'voucher' && campaign.voucherCode && (
            <Row label="Cod voucher">
              <span className="font-mono">
                {campaign.voucherCode} · −{campaign.voucherPct}%
              </span>
            </Row>
          )}
          <Row label="Perioadă">{formatPeriod(campaign.startDate, campaign.endDate)}</Row>
          <Row label="Buget DoAds">
            {campaign.budget !== null ? (
              <span className="font-mono">{campaign.budget.toLocaleString('ro-RO')} EUR</span>
            ) : (
              '—'
            )}
          </Row>
        </div>
      </div>
    </div>
  )
}

function BriefSentView({
  campaign,
  onOpenBriefModal,
  onOpenEmailModal,
}: {
  campaign: MkCampaign
  onOpenBriefModal: () => void
  onOpenEmailModal: (emailType: EmailType) => void
}) {
  const sent = campaign.sentEmails?.brief_360
  return (
    <div className="space-y-4 rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-b4l-text">Brief către 360 Advertising</h4>
        <span
          className={`rounded text-white text-[10px] font-semibold px-2 py-0.5 ${
            sent ? 'bg-b4l-green' : 'bg-amber-500'
          }`}
        >
          {sent ? 'Email trimis' : 'Email netrimis'}
        </span>
      </div>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={onOpenBriefModal}>
          📖 Vezi brief
        </Button>
      </div>

      {sent ? (
        <EmailCard
          direction="outbound"
          meta={`📤 Trimis · CRM AI → ${sent.to}`}
          subject={sent.subject}
          body={`Email referință: ${sent.b4lId}. Conținutul complet a fost generat de AI și trimis prin SMTP-ul B4L.`}
        />
      ) : (
        <div className="space-y-3 rounded border border-amber-300 bg-amber-50 p-4 text-sm">
          <div className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <strong>Brieful nu a ajuns încă la 360.</strong>
              <div className="mt-1 text-xs">
                Campania e la Pas 2 dar emailul către agenție nu a fost trimis efectiv. Apasă
                butonul de mai jos ca să-l previzualizezi și să-l trimiți acum.
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => onOpenEmailModal('brief_360')}>
              <Mail className="h-3.5 w-3.5" /> Trimite brief către 360 →
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ProductionView({
  campaign,
  isInteractive,
  onOpenMarketingModal,
}: {
  campaign: MkCampaign
  isInteractive: boolean
  onOpenMarketingModal: () => void
}) {
  const done = campaign.bannersDone
  const messages = (campaign.marketingMessages ?? null) as Record<string, string> | null
  const allDone = done === 9
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-b4l-text">Storage Share · upload 360</h4>
          <span
            className={`rounded text-[10px] font-semibold px-2 py-0.5 text-white ${
              allDone ? 'bg-b4l-green' : 'bg-amber-500'
            }`}
          >
            {done}/9 bannere
          </span>
        </div>
        {isInteractive ? (
          <BannereTab campaign={campaign} />
        ) : (
          <BannereSummary campaign={campaign} />
        )}
      </div>

      {allDone && messages && (
        <button
          type="button"
          onClick={onOpenMarketingModal}
          className="block w-full rounded-lg border-2 border-b4l-purple bg-white p-4 text-left transition hover:bg-b4l-purple-light"
        >
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded bg-b4l-purple text-white">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <div className="text-sm font-semibold text-b4l-text">
                Marketing copy · 4 mesaje generate de AI
              </div>
              <div className="text-[11px] text-b4l-text-muted">
                Email Launch + Last Call (Omnisend) + SMS + WhatsApp (Chatarmin)
              </div>
            </div>
          </div>
          {messages.email1 && (
            <div className="rounded border-l-4 border-b4l-purple bg-b4l-bg-soft px-3 py-2 text-[11px] text-b4l-text">
              <span className="font-semibold">Email Launch:</span>{' '}
              {messages.email1.split('\n')[0].slice(0, 100)}…
            </div>
          )}
          <div className="mt-2 text-[11px] font-semibold text-b4l-purple-dark">
            ✨ Review / editează → trimitere în Pas 5
          </div>
        </button>
      )}

      {allDone && !messages && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div>
              Toate cele 9 bannere sunt încărcate, dar textele de marketing nu sunt generate încă.
              Apasă „Regenerează textele de marketing” în grila de bannere de mai sus.
            </div>
          </div>
        </div>
      )}

      {!allDone && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div>
              Se așteaptă bannerele de la 360. Când ajunge <strong>1200×628</strong> (ad-banner), AI
              compune automat mesajele de email/SMS/WhatsApp.
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function BannereSummary({ campaign }: { campaign: MkCampaign }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {campaign.banners.map((b) => (
        <div
          key={b.id}
          className={`rounded border p-2 text-xs ${
            b.status === 'done'
              ? 'border-b4l-green bg-b4l-green-light'
              : 'border-b4l-border bg-b4l-bg-soft'
          }`}
        >
          <div className="font-semibold text-b4l-text">{b.slug}</div>
          <div className="text-[10px] text-b4l-text-muted">{b.size}</div>
          <div className="text-[10px] text-b4l-text-dim">
            {b.status === 'done' ? '✓ urcat' : '— lipsă'}
          </div>
        </div>
      ))}
    </div>
  )
}

function LansareView({
  campaign,
  onOpenMarketingModal,
  onOpenEmailModal,
}: {
  campaign: MkCampaign
  onOpenMarketingModal: () => void
  onOpenEmailModal: (emailType: EmailType) => void
}) {
  const wbSent = campaign.sentEmails?.lansare_webblast
  const daSent = campaign.sentEmails?.lansare_doads
  const wbConfirmed = campaign.webBlastConfirmed
  const daConfirmed = campaign.doAdsConfirmed
  const messages = (campaign.marketingMessages ?? null) as Record<string, string> | null

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-b4l-text">Pachet la WebBlast & DoAds</h4>
          <span
            className={`rounded text-[10px] font-semibold px-2 py-0.5 text-white ${
              wbConfirmed && daConfirmed ? 'bg-b4l-green' : 'bg-amber-500'
            }`}
          >
            {wbConfirmed && daConfirmed ? 'Lansat' : 'Așteaptă răspunsuri'}
          </span>
        </div>
        <p className="text-[11px] text-b4l-text-muted">
          Emailurile conțin link-urile Storage Share confirmate la Pas 3. Când ambele agenții
          răspund, campania pornește automat.
        </p>
      </div>

      {messages && (
        <button
          type="button"
          onClick={onOpenMarketingModal}
          className="block w-full rounded-lg border-2 border-b4l-purple bg-white p-3 text-left transition hover:bg-b4l-purple-light"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded bg-b4l-purple text-white">
              <Mail className="h-3.5 w-3.5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-b4l-text">Marketing copy (4 mesaje)</div>
              <div className="text-[11px] text-b4l-text-muted">
                Trimitere: ziua 2 (Email 1 + SMS + WA) · penultima zi (Email 2)
              </div>
            </div>
            <span className="ml-auto text-[11px] font-semibold text-b4l-purple-dark">
              ✨ Review / editează →
            </span>
          </div>
        </button>
      )}

      <AgencyEmailSection
        title="WebBlast"
        sent={wbSent}
        confirmed={wbConfirmed}
        confirmDesc={
          campaign.type === 'pret_taiat'
            ? 'Bannerele sunt instalate, prețurile tăiate sunt configurate.'
            : 'Bannerele sunt instalate, codul de voucher e activ.'
        }
        onPrepareEmail={() => onOpenEmailModal('lansare_webblast')}
      />

      <AgencyEmailSection
        title="DoAds"
        sent={daSent}
        confirmed={daConfirmed}
        confirmDesc="Campaniile Google Ads și Meta Ads au pornit, cu data de sfârșit setată."
        onPrepareEmail={() => onOpenEmailModal('lansare_doads')}
      />
    </div>
  )
}

function AgencyEmailSection({
  title,
  sent,
  confirmed,
  confirmDesc,
  onPrepareEmail,
}: {
  title: string
  sent: { sentAt: string; b4lId: string; to: string; subject: string } | undefined
  confirmed: boolean
  confirmDesc: string
  onPrepareEmail: () => void
}) {
  return (
    <div className="space-y-2">
      <h5 className="text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
        {title}
      </h5>
      {sent ? (
        <EmailCard
          direction="outbound"
          meta={`📤 ${sent.to} · ${sent.b4lId}`}
          subject={sent.subject}
          body={`Trimis ${new Date(sent.sentAt).toLocaleString('ro-RO')}`}
        />
      ) : (
        <div className="flex items-center justify-between rounded border border-dashed border-b4l-border bg-white p-3 text-xs">
          <span className="text-b4l-text-muted">Email nepregătit încă</span>
          <Button variant="secondary" onClick={onPrepareEmail}>
            <Mail className="h-3.5 w-3.5" /> Pregătește
          </Button>
        </div>
      )}
      {confirmed ? (
        <EmailCard
          direction="inbound"
          meta="📥 Răspuns parsat de AI"
          subject=""
          body={confirmDesc}
        />
      ) : sent ? (
        <div className="rounded border border-dashed border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
          ⏳ Așteaptă răspuns {title}
        </div>
      ) : null}
    </div>
  )
}

function LiveView({
  campaign,
  onOpenMarketingModal,
}: {
  campaign: MkCampaign
  onOpenMarketingModal: () => void
}) {
  const dp = getDayProgress(campaign)
  const ms = (campaign.marketingSent ?? {}) as Record<string, boolean>
  const isDay2 = dp?.day === 2
  const isPenultimate = dp && dp.day === dp.total - 1

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-b4l-text">Campanie activă</h4>
          <span className="rounded bg-b4l-green text-[10px] font-semibold px-2 py-0.5 text-white">
            Live
          </span>
        </div>
        <div className="space-y-1.5 text-xs">
          <Row label="Stare">
            <span className="text-b4l-green-dark">
              🟢 Live{dp ? ` · ziua ${dp.day}/${dp.total}` : ''}
            </span>
          </Row>
          <Row label="Perioadă">{formatPeriod(campaign.startDate, campaign.endDate)}</Row>
          <Row label="Tip">{campaign.type === 'pret_taiat' ? 'Preț tăiat' : 'Voucher'}</Row>
          <Row label="Buget">
            {campaign.budget !== null ? (
              <span className="font-mono">{campaign.budget.toLocaleString('ro-RO')} EUR</span>
            ) : (
              '—'
            )}
          </Row>
        </div>
      </div>

      <div>
        <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Marketing pe canale
        </h5>
        <MarketingStatusGrid sent={ms} />
        <div className="mt-2 flex justify-end">
          <Button variant="secondary" onClick={onOpenMarketingModal}>
            <Mail className="h-3.5 w-3.5" /> Deschide mesajele
          </Button>
        </div>
      </div>

      {isDay2 && (!ms.email1 || !ms.sms || !ms.whatsapp) && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div className="flex-1">
              <strong>Astăzi (ziua 2)</strong> trebuie trimise:{' '}
              {[!ms.email1 && 'Email 1', !ms.sms && 'SMS', !ms.whatsapp && 'WhatsApp']
                .filter(Boolean)
                .join(' + ')}
              .
              <div className="mt-2">
                <Button onClick={onOpenMarketingModal}>
                  <Mail className="h-3.5 w-3.5" /> Deschide mesajele →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isPenultimate && !ms.email2 && (
        <div className="rounded-lg border border-amber-400 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="flex items-start gap-2">
            <Clock className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
            <div className="flex-1">
              <strong>Astăzi (penultima zi)</strong> trebuie trimis: Email 2 (Last call).
              <div className="mt-2">
                <Button onClick={onOpenMarketingModal}>
                  <Mail className="h-3.5 w-3.5" /> Deschide mesajul →
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded border border-blue-300 bg-blue-50 p-3 text-[11px] text-blue-900">
        CRM nu primește date live. La end date AI cere automat raportul DoAds.
      </div>
    </div>
  )
}

function MarketingStatusGrid({ sent }: { sent: Record<string, boolean> }) {
  const items = [
    { key: 'email1', label: 'Email 1 Launch', target: 'Omnisend · ziua 2' },
    { key: 'email2', label: 'Email 2 Last call', target: 'Omnisend · penultima zi' },
    { key: 'sms', label: 'SMS', target: 'Omnisend · ziua 2' },
    { key: 'whatsapp', label: 'WhatsApp', target: 'Chatarmin · ziua 2' },
  ]
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      {items.map((it) => {
        const isSent = !!sent[it.key]
        return (
          <div
            key={it.key}
            className={`rounded border p-2.5 text-xs ${
              isSent ? 'border-b4l-green bg-b4l-green-light' : 'border-b4l-border bg-white'
            }`}
          >
            <div className="flex items-start gap-1.5">
              <div
                className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                  isSent ? 'bg-b4l-green text-white' : 'bg-b4l-bg text-b4l-text-muted'
                }`}
              >
                {isSent ? '✓' : '○'}
              </div>
              <div>
                <div className="font-semibold text-b4l-text">{it.label}</div>
                <div className="text-[10px] text-b4l-text-muted">{it.target}</div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ClosedView({ campaign }: { campaign: MkCampaign }) {
  const reportSent = campaign.sentEmails?.cerere_raport_doads
  const scoatSent = campaign.sentEmails?.cerere_scoatere_webblast
  const reportData = campaign.reportData
  const ms = campaign.marketingSent ?? {}

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-b4l-border bg-b4l-bg-soft p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-b4l-text">Închidere · {campaign.name}</h4>
          <span
            className={`rounded text-[10px] font-semibold px-2 py-0.5 text-white ${
              campaign.finalized ? 'bg-b4l-green' : 'bg-amber-500'
            }`}
          >
            {campaign.finalized ? 'Finalizată automat' : 'Așteaptă emailuri'}
          </span>
        </div>
      </div>

      <div>
        <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Auto la end date
        </h5>
        <div className="space-y-1.5 rounded border border-b4l-border bg-white p-3 text-xs">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-b4l-green" />
            <span>
              {campaign.type === 'pret_taiat'
                ? 'NA Bulk Price Editor a revertat prețurile.'
                : 'Cod voucher dezactivat.'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5 text-b4l-green" />
            <span>DoAds: campaniile s-au oprit la end date.</span>
          </div>
        </div>
      </div>

      <div>
        <h5 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Marketing trimis
        </h5>
        <MarketingStatusGrid sent={ms} />
      </div>

      <div className="space-y-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Email DoAds · raport final
        </h5>
        {reportSent ? (
          <EmailCard
            direction="outbound"
            meta={`📤 ${reportSent.to} · ${reportSent.b4lId}`}
            subject={reportSent.subject}
            body={`Trimis ${new Date(reportSent.sentAt).toLocaleString('ro-RO')}`}
          />
        ) : (
          <div className="rounded border border-dashed border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
            Email nepregătit încă
          </div>
        )}
        {campaign.reportReceived && reportData ? (
          <div className="rounded-lg border border-b4l-green bg-b4l-green-light p-3 text-xs">
            <div className="mb-2 font-semibold text-b4l-green-dark">📥 Raport parsat automat</div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
              <ReportStat label="Comenzi" value={String(reportData.orders ?? '—')} />
              <ReportStat
                label="Vânzări"
                value={
                  reportData.sales != null ? `${reportData.sales.toLocaleString('ro-RO')} €` : '—'
                }
              />
              <ReportStat
                label="Cheltuit"
                value={
                  reportData.spent != null ? `${reportData.spent.toLocaleString('ro-RO')} €` : '—'
                }
              />
              <ReportStat
                label="ROAS"
                value={reportData.roas != null ? `${reportData.roas}×` : '—'}
              />
            </div>
          </div>
        ) : reportSent ? (
          <div className="rounded border border-dashed border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
            ⏳ Așteaptă răspuns DoAds
          </div>
        ) : null}
      </div>

      <div className="space-y-2">
        <h5 className="text-[11px] font-semibold uppercase tracking-wide text-b4l-text-muted">
          Email WebBlast · scoatere bannere
        </h5>
        {scoatSent ? (
          <EmailCard
            direction="outbound"
            meta={`📤 ${scoatSent.to} · ${scoatSent.b4lId}`}
            subject={scoatSent.subject}
            body={`Trimis ${new Date(scoatSent.sentAt).toLocaleString('ro-RO')}`}
          />
        ) : (
          <div className="rounded border border-dashed border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
            Email nepregătit încă
          </div>
        )}
        {campaign.bannersRemoved ? (
          <EmailCard
            direction="inbound"
            meta="📥 Confirmare primită"
            subject=""
            body="Bannerele au fost scoase de pe site."
          />
        ) : scoatSent ? (
          <div className="rounded border border-dashed border-b4l-border bg-white p-3 text-xs italic text-b4l-text-muted">
            ⏳ Așteaptă răspuns WebBlast
          </div>
        ) : null}
      </div>

      {campaign.reportReceived && campaign.bannersRemoved && !campaign.finalized && (
        <div className="rounded-lg border border-b4l-green bg-b4l-green-light p-3 text-xs text-b4l-green-dark">
          <strong>✓ Ambele emailuri parsate.</strong> Campania se finalizează automat...
        </div>
      )}
    </div>
  )
}

function ReportStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded border border-b4l-green bg-white p-2 text-center">
      <div className="text-[10px] uppercase text-b4l-text-muted">{label}</div>
      <div className="mt-1 flex items-center justify-center gap-1 text-base font-bold text-b4l-text">
        <TrendingUp className="h-3 w-3 text-b4l-green-dark" />
        {value}
      </div>
    </div>
  )
}

function EmailCard({
  direction,
  meta,
  subject,
  body,
}: {
  direction: 'outbound' | 'inbound'
  meta: string
  subject: string
  body: string
}) {
  return (
    <div
      className={`rounded border p-3 text-xs ${
        direction === 'outbound'
          ? 'border-b4l-border bg-white'
          : 'border-b4l-green bg-b4l-green-light'
      }`}
    >
      <div className="mb-1 text-[10px] font-semibold text-b4l-text-muted">{meta}</div>
      {subject && <div className="mb-1 text-sm font-semibold text-b4l-text">{subject}</div>}
      <div className="whitespace-pre-wrap text-b4l-text">{body}</div>
    </div>
  )
}

type EmailType =
  | 'brief_360'
  | 'lansare_webblast'
  | 'lansare_doads'
  | 'cerere_raport_doads'
  | 'cerere_scoatere_webblast'

interface EmailDef {
  type: EmailType
  label: string
  agencyHint: string
  availableFromStep: number
  finalStep: boolean
}

const CAMPAIGN_EMAILS: EmailDef[] = [
  {
    type: 'brief_360',
    label: 'Brief către 360 Advertising',
    agencyHint: 'design — 9 bannere',
    availableFromStep: 1,
    finalStep: false,
  },
  {
    type: 'lansare_webblast',
    label: 'Lansare WebBlast',
    agencyHint: 'shopify ops — site + voucher/pret',
    availableFromStep: 3,
    finalStep: false,
  },
  {
    type: 'lansare_doads',
    label: 'Lansare DoAds',
    agencyHint: 'ads ops — Google + Meta',
    availableFromStep: 3,
    finalStep: false,
  },
  {
    type: 'cerere_raport_doads',
    label: 'Cerere raport DoAds',
    agencyHint: 'final campanie',
    availableFromStep: 5,
    finalStep: true,
  },
  {
    type: 'cerere_scoatere_webblast',
    label: 'Scoatere bannere WebBlast',
    agencyHint: 'final campanie',
    availableFromStep: 5,
    finalStep: true,
  },
]

interface AiBriefResponse {
  rawText: string
  fileName: string
  ai: {
    tagline: string | null
    briefContent: string
    startDate: string | null
    endDate: string | null
    productSuggestions: Array<{ sku: string; discountPct: number }>
    voucherCode: string | null
    voucherPct: number | null
    type: 'pret_taiat' | 'voucher' | null
    notes: string | null
  }
}

function BriefAiModal({ campaign, onClose }: { campaign: MkCampaign; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [briefContent, setBriefContent] = useState(campaign.briefContent ?? '')
  const [tagline, setTagline] = useState(campaign.tagline ?? '')
  const [aiPreview, setAiPreview] = useState<AiBriefResponse['ai'] | null>(null)
  const [aiInstruction, setAiInstruction] = useState('')
  const [aiLoadingContext, setAiLoadingContext] = useState<string | null>(null)
  // Generare brief de la zero (alternativa la upload .docx).
  const [generateMode, setGenerateMode] = useState(false)
  const [generatePrompt, setGeneratePrompt] = useState('')

  const fileInputRef = (
    <input
      type="file"
      accept=".docx"
      onChange={(e) => {
        const file = e.target.files?.[0]
        if (file) parseMutation.mutate(file)
      }}
      className="hidden"
      id="brief-file-input"
    />
  )

  const parseMutation = useMutation({
    mutationFn: async (file: File): Promise<AiBriefResponse> => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post(
        `/marketing/campaigns-mk/${campaign.id}/parse-brief`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return data
    },
    onSuccess: (data) => {
      setAiPreview(data.ai)
      setBriefContent(data.ai.briefContent)
      if (data.ai.tagline) setTagline(data.ai.tagline)
      toast.success(`Brief "${data.fileName}" parsat de AI`)
    },
    onError: (err: unknown) => {
      toast.error(extractServerError(err, 'Eroare la parsare brief'))
    },
  })

  const applyMutation = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { briefContent, tagline: tagline || null }
      if (aiPreview) {
        if (aiPreview.startDate) body.startDate = aiPreview.startDate
        if (aiPreview.endDate) body.endDate = aiPreview.endDate
        if (aiPreview.type) body.type = aiPreview.type
        if (aiPreview.voucherCode) body.voucherCode = aiPreview.voucherCode
        if (aiPreview.voucherPct !== null) body.voucherPct = aiPreview.voucherPct
        // Filtru defensiv: pastram doar produse cu SKU valid (discountPct poate fi null/missing
        // pentru campanii fara reducere — backend default-eaza la 0).
        const validProducts = aiPreview.productSuggestions.filter(
          (p) => typeof p.sku === 'string' && p.sku.length > 0,
        )
        if (validProducts.length > 0) body.productSuggestions = validProducts
      }
      return (await api.post(`/marketing/campaigns-mk/${campaign.id}/apply-brief`, body)).data
    },
    onSuccess: () => {
      toast.success('Brief salvat')
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaign.id] })
      onClose()
    },
    onError: (err: unknown) => {
      toast.error(extractServerError(err, 'Eroare la salvare brief'))
    },
  })

  const callAiAssist = async (context: string, instruction: string) => {
    setAiLoadingContext(context)
    try {
      const { data } = await api.post(`/marketing/campaigns-mk/${campaign.id}/ai-assist`, {
        context,
        currentText: context === 'tagline_alternative' ? tagline : briefContent,
        instruction,
      })
      if (context === 'tagline_alternative') setTagline(data.result)
      else setBriefContent(data.result)
      if (context === 'brief_generate') {
        setGenerateMode(false)
        toast.success('Brief generat de AI — verifică și ajustează')
      } else {
        toast.success('AI a generat o variantă nouă')
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'AI a eșuat')
    } finally {
      setAiLoadingContext(null)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Brief & AI assistant"
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button
            onClick={() => applyMutation.mutate()}
            disabled={!briefContent || applyMutation.isPending}
          >
            {applyMutation.isPending ? 'Se salvează...' : 'Salvează brief'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Surse brief: upload .docx sau generare de la zero cu AI */}
        <div className="rounded border border-dashed border-b4l-border bg-b4l-bg-soft p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1">
              <div className="text-sm font-semibold text-b4l-text">Sursă brief</div>
              <div className="text-xs text-b4l-text-muted">
                Încarcă un .docx existent (AI parsează) sau generează unul de la zero cu AI
              </div>
            </div>
            <div className="flex items-center gap-2">
              {fileInputRef}
              <label htmlFor="brief-file-input">
                <span className="inline-flex cursor-pointer items-center gap-2 rounded bg-b4l-blue px-3 py-2 text-sm font-medium text-white hover:bg-b4l-blue-dark">
                  {parseMutation.isPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Se parsează...
                    </>
                  ) : (
                    <>
                      <Upload className="h-3.5 w-3.5" /> Selectează .docx
                    </>
                  )}
                </span>
              </label>
              <button
                type="button"
                onClick={() => setGenerateMode((v) => !v)}
                className="inline-flex items-center gap-2 rounded bg-b4l-purple px-3 py-2 text-sm font-medium text-white hover:bg-b4l-purple-dark"
              >
                <Wand2 className="h-3.5 w-3.5" />
                {generateMode ? 'Ascunde' : 'Generează cu AI'}
              </button>
            </div>
          </div>

          {generateMode && (
            <div className="mt-3 space-y-2 border-t border-b4l-border pt-3">
              <label className="block text-xs font-medium text-b4l-text-muted">
                Detalii suplimentare (opțional) — context, target, mesaj-cheie
              </label>
              <textarea
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                rows={3}
                placeholder="ex: Ținta — femei 30-45 ani, mesaj-cheie hidratare profundă, vrem accent pe ritualul de seară. (Opțional — AI folosește numele campaniei + tipul + perioada chiar și fără descriere.)"
                className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
              />
              <div className="flex justify-end">
                <Button
                  onClick={() =>
                    callAiAssist(
                      'brief_generate',
                      generatePrompt ||
                        'Foloseste contextul campaniei pentru a genera un brief complet, structurat pe sectiuni.',
                    )
                  }
                  disabled={aiLoadingContext === 'brief_generate'}
                >
                  {aiLoadingContext === 'brief_generate' ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Wand2 className="h-3.5 w-3.5" />
                  )}
                  Generează brief
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* AI preview */}
        {aiPreview && (
          <div className="rounded border border-b4l-green bg-b4l-green-light p-3 text-xs">
            <div className="mb-2 font-semibold text-b4l-green-dark">AI a sugerat:</div>
            <ul className="space-y-1 text-b4l-green-dark">
              {aiPreview.tagline && (
                <li>
                  • Tagline: <strong>{aiPreview.tagline}</strong>
                </li>
              )}
              {aiPreview.type && (
                <li>
                  • Tip: <strong>{aiPreview.type === 'voucher' ? 'Voucher' : 'Preț tăiat'}</strong>
                </li>
              )}
              {(aiPreview.startDate || aiPreview.endDate) && (
                <li>
                  • Perioadă:{' '}
                  <strong>
                    {aiPreview.startDate ?? '?'} → {aiPreview.endDate ?? '?'}
                  </strong>
                </li>
              )}
              {aiPreview.voucherCode && (
                <li>
                  • Voucher: <strong>{aiPreview.voucherCode}</strong> ({aiPreview.voucherPct ?? '?'}
                  %)
                </li>
              )}
              {aiPreview.productSuggestions.length > 0 && (
                <li>
                  • Produse: <strong>{aiPreview.productSuggestions.length}</strong> sugerate (
                  {aiPreview.productSuggestions
                    .slice(0, 3)
                    .map((p) => `${p.sku} −${p.discountPct}%`)
                    .join(', ')}
                  {aiPreview.productSuggestions.length > 3 && '...'})
                </li>
              )}
              {aiPreview.notes && (
                <li>
                  • Note: <em>{aiPreview.notes}</em>
                </li>
              )}
            </ul>
            <div className="mt-2 text-[10px] italic text-b4l-green-dark">
              Toate aceste valori vor fi aplicate la &laquo;Salvează brief&raquo;.
            </div>
          </div>
        )}

        {/* Tagline */}
        <FormField label="Tagline">
          <div className="flex gap-2">
            <input
              type="text"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="ex: Echilibru pentru sărbători"
              className="flex-1 rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
            <Button
              variant="secondary"
              onClick={() =>
                callAiAssist(
                  'tagline_alternative',
                  aiInstruction || 'Genereaza un tagline alternativ, max 80 caractere, in romana.',
                )
              }
              disabled={aiLoadingContext === 'tagline_alternative'}
            >
              {aiLoadingContext === 'tagline_alternative' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5" />
              )}
              AI
            </Button>
          </div>
        </FormField>

        {/* Brief content */}
        <FormField label="Conținut brief (editabil)">
          <textarea
            value={briefContent}
            onChange={(e) => setBriefContent(e.target.value)}
            rows={10}
            placeholder="Detaliile campaniei: produse, preturi, perioadă, target, mesaje cheie..."
            className="w-full rounded border border-b4l-border px-3 py-2 font-mono text-xs outline-none focus:border-b4l-green"
          />
        </FormField>

        {/* AI assist for content */}
        <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
          <div className="mb-2 text-xs font-semibold text-b4l-text">AI assistant — conținut</div>
          <div className="flex flex-wrap gap-1.5">
            {[
              {
                ctx: 'rewrite_concise',
                label: 'Mai concis',
                instr: 'Rescrie textul mai concis, păstrând esențialul.',
              },
              {
                ctx: 'rewrite_warm',
                label: 'Mai cald',
                instr: 'Rescrie cu un ton mai prietenos și cald.',
              },
              {
                ctx: 'cta_suggestion',
                label: 'Sugerează CTA',
                instr: 'Adaugă un call-to-action puternic la final.',
              },
              {
                ctx: 'disclaimer',
                label: 'Adaugă disclaimer',
                instr: 'Adaugă un disclaimer scurt și formal pentru OUG 84/2022.',
              },
            ].map((preset) => (
              <button
                key={preset.ctx}
                type="button"
                onClick={() => callAiAssist(preset.ctx, preset.instr)}
                disabled={aiLoadingContext !== null}
                className="rounded border border-b4l-border bg-white px-2 py-1 text-xs hover:bg-b4l-bg disabled:opacity-50"
              >
                {aiLoadingContext === preset.ctx ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  preset.label
                )}
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-2">
            <input
              type="text"
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
              placeholder="Instrucțiune custom AI..."
              className="flex-1 rounded border border-b4l-border px-3 py-1.5 text-xs outline-none focus:border-b4l-green"
            />
            <Button
              variant="secondary"
              onClick={() =>
                aiInstruction
                  ? callAiAssist('free_form', aiInstruction)
                  : toast.error('Adaugă o instrucțiune')
              }
              disabled={aiLoadingContext === 'free_form'}
            >
              {aiLoadingContext === 'free_form' ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Wand2 className="h-3.5 w-3.5" />
              )}
              Aplică
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

function BannereTab({ campaign }: { campaign: MkCampaign }) {
  const queryClient = useQueryClient()
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null)

  const uploadMutation = useMutation({
    mutationFn: async ({ slug, file }: { slug: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const { data } = await api.post(
        `/marketing/campaigns-mk/${campaign.id}/banners/${slug}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaign.id] })
      if (data.marketingCopyTriggered) {
        toast.success('Banner urcat. AI generează mesajele de marketing automat...')
      } else {
        toast.success('Banner urcat')
      }
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Bannerul nu s-a putut încărca')
    },
    onSettled: () => setUploadingSlug(null),
  })

  const syncMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/campaigns-mk/${campaign.id}/sync-banners`)).data as {
        synced: number
        alreadyDone: number
        notFound: string[]
      },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaign.id] })
      toast.success(
        `Sincronizare: ${data.synced} bannere actualizate, ${data.alreadyDone} erau deja încărcate${
          data.notFound.length > 0 ? `, ${data.notFound.length} încă lipsă` : ''
        }`,
      )
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Sincronizarea nu a reușit'),
  })

  const generateCopyMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/campaigns-mk/${campaign.id}/generate-marketing-copy`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaign.id] })
      toast.success('Marketing copy generat de AI')
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Generare eșuată'),
  })

  const handleSelectFile = (slug: string) => {
    setUploadingSlug(slug)
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = () => {
      const file = input.files?.[0]
      if (file) uploadMutation.mutate({ slug, file })
      else setUploadingSlug(null)
    }
    input.click()
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-b4l-text-muted">
          9 formate standard. Apasă pe un format ca să încarci bannerul manual, sau folosește
          „Sincronizează din Storage Share” dacă agenția 360 a urcat fișierele direct acolo.
        </p>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Search className="h-3.5 w-3.5" />
            )}
            Sincronizează din Storage Share
          </Button>
          <Button
            onClick={() => generateCopyMutation.mutate()}
            disabled={generateCopyMutation.isPending}
          >
            {generateCopyMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            Regenerează textele de marketing
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {campaign.banners.map((b) => {
          const isAdBanner = b.slug === 'ad-banner'
          const isUploading = uploadingSlug === b.slug && uploadMutation.isPending
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => handleSelectFile(b.slug)}
              disabled={isUploading}
              className={`group flex flex-col rounded border p-2 text-left text-xs transition ${
                b.status === 'done'
                  ? 'border-b4l-green bg-b4l-green-light hover:border-b4l-green-dark'
                  : 'border-dashed border-b4l-border bg-b4l-bg-soft hover:border-b4l-blue hover:bg-white'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="font-semibold text-b4l-text">{b.slug}</div>
                {isAdBanner && (
                  <span className="rounded bg-b4l-purple-light px-1.5 py-0.5 text-[9px] font-semibold text-b4l-purple-dark">
                    AI trigger
                  </span>
                )}
              </div>
              <div className="text-[10px] text-b4l-text-muted">{b.size}</div>
              <div className="text-[10px] text-b4l-text-dim">{b.target}</div>
              {b.status === 'done' && b.fileUrl ? (
                <div className="mt-2 flex h-16 items-center justify-center rounded bg-white text-[10px] text-b4l-green-dark">
                  ✓ {b.fileUrl.split('/').pop()?.slice(0, 30)}
                </div>
              ) : (
                <div className="mt-2 flex h-16 items-center justify-center rounded bg-white/40 text-[10px] text-b4l-text-muted">
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <span className="opacity-60 group-hover:opacity-100">
                      Apasă pentru a încărca
                    </span>
                  )}
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface MarketingMessages {
  email1?: string
  email2?: string
  sms?: string
  whatsapp?: string
}

function MarketingTab({ campaign }: { campaign: MkCampaign }) {
  const queryClient = useQueryClient()
  const messages = (campaign.marketingMessages ?? null) as MarketingMessages | null
  const [activeTab, setActiveTab] = useState<keyof MarketingMessages>('email1')

  const generateMutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/marketing/campaigns-mk/${campaign.id}/generate-marketing-copy`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaign.id] })
      toast.success('Marketing copy generat')
    },
    onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Generare eșuată'),
  })

  if (!messages) {
    return (
      <div className="space-y-3">
        <p className="text-sm italic text-b4l-text-muted">
          Niciun text de marketing generat încă. Mesajele vor fi generate automat când sosește
          bannerul 1200×628 (ad-banner) sau le poți genera manual acum.
        </p>
        <Button onClick={() => generateMutation.mutate()} disabled={generateMutation.isPending}>
          {generateMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wand2 className="h-3.5 w-3.5" />
          )}
          Generează acum
        </Button>
      </div>
    )
  }

  const labels: Record<keyof MarketingMessages, string> = {
    email1: 'Email 1 (lansare)',
    email2: 'Email 2 (last call)',
    sms: 'SMS (160 char)',
    whatsapp: 'WhatsApp',
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copiat în clipboard`)
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1 border-b border-b4l-border">
        {(['email1', 'email2', 'sms', 'whatsapp'] as Array<keyof MarketingMessages>).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveTab(k)}
            className={`px-3 py-1.5 text-xs transition ${
              activeTab === k
                ? 'border-b-2 border-b4l-green font-semibold text-b4l-text'
                : 'text-b4l-text-muted hover:text-b4l-text'
            }`}
          >
            {labels[k]}
          </button>
        ))}
      </div>

      <div className="rounded border border-b4l-border bg-white p-3">
        <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap font-mono text-xs text-b4l-text">
          {messages[activeTab] ?? '(niciun conținut)'}
        </pre>
      </div>

      <div className="flex justify-between gap-2">
        <Button
          variant="secondary"
          onClick={() => copyToClipboard(messages[activeTab] ?? '', labels[activeTab])}
        >
          Copiază
        </Button>
        <Button
          variant="secondary"
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Wand2 className="h-3.5 w-3.5" />
          )}
          Re-generează tot
        </Button>
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex">
      <span className="w-32 flex-shrink-0 text-xs text-b4l-text-muted">{label}</span>
      <span className="flex-1 text-b4l-text">{children}</span>
    </div>
  )
}

function FormField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-b4l-text-muted">{label}</label>
      {children}
    </div>
  )
}

interface ComposedEmailResponse {
  to: string
  agencyName: string
  agencyRole: string
  subject: string
  body: string
}

function EmailPreviewModal({
  campaignId,
  emailType,
  emailLabel,
  onClose,
}: {
  campaignId: number
  emailType: EmailType
  emailLabel: string
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [to, setTo] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [agencyName, setAgencyName] = useState('')
  const [aiInstruction, setAiInstruction] = useState('')
  const [composeError, setComposeError] = useState<string | null>(null)

  const composeMutation = useMutation({
    mutationFn: async (customInstruction?: string) => {
      const { data } = await api.post<ComposedEmailResponse>(
        `/marketing/campaigns-mk/${campaignId}/compose-email`,
        { emailType, ...(customInstruction && { customInstruction }) },
      )
      return data
    },
    onSuccess: (data) => {
      setTo(data.to)
      setSubject(data.subject)
      setBody(data.body)
      setAgencyName(data.agencyName)
      setComposeError(null)
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Compose AI a eșuat'
      setComposeError(msg)
    },
  })

  const sendMutation = useMutation({
    mutationFn: async () => {
      const { data } = await api.post(`/marketing/campaigns-mk/${campaignId}/send-email`, {
        emailType,
        to,
        subject,
        body,
      })
      return data as { b4lId: string; advancedToStep?: number }
    },
    onSuccess: (data) => {
      toast.success(
        `Email trimis (${data.b4lId})${data.advancedToStep ? ` — campanie avansată la pasul ${data.advancedToStep}` : ''}`,
      )
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['mk-campaigns', campaignId] })
      onClose()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'Trimitere email eșuată')
    },
  })

  // Auto-compose la deschidere modal
  useEffect(() => {
    composeMutation.mutate(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const recompose = (instruction: string) => {
    composeMutation.mutate(instruction)
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${emailLabel}${agencyName ? ` — ${agencyName}` : ''}`}
      size="xl"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <span className="text-xs text-b4l-text-muted">
            Emailul pleacă din contul CRM. Răspunsurile agenției sunt citite automat.
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Anulează
            </Button>
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={
                !to || !subject || !body || sendMutation.isPending || composeMutation.isPending
              }
            >
              {sendMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Trimite email
            </Button>
          </div>
        </div>
      }
    >
      {composeError ? (
        <div className="space-y-3">
          <div className="rounded border border-b4l-red bg-b4l-red-light p-3 text-sm text-b4l-red-dark">
            <div className="font-semibold">Agenția nu e configurată</div>
            <div className="mt-1 text-xs">{composeError}</div>
            <div className="mt-2 text-xs">
              Mergi în{' '}
              <a
                href="/setari/agentii-marketing"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold underline"
              >
                Setări → Agenții marketing
              </a>{' '}
              și adaugă agenția cu rolul corespunzător (design / shopify_ops / ads_ops). Apoi revino
              aici și apasă &laquo;Re-încearcă&raquo;.
            </div>
          </div>
          <Button variant="secondary" onClick={() => composeMutation.mutate(undefined)}>
            Re-încearcă
          </Button>
        </div>
      ) : composeMutation.isPending && !body ? (
        <div className="flex items-center gap-2 p-6 text-sm text-b4l-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          AI compune emailul...
        </div>
      ) : (
        <div className="space-y-3">
          <FormField label="Destinatar (To)">
            <input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
          </FormField>
          <FormField label="Subiect">
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm outline-none focus:border-b4l-green"
            />
          </FormField>
          <FormField label="Corp email (editabil)">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="w-full rounded border border-b4l-border px-3 py-2 font-mono text-xs outline-none focus:border-b4l-green"
            />
          </FormField>

          <div className="rounded border border-b4l-border bg-b4l-bg-soft p-3">
            <div className="mb-2 text-xs font-semibold text-b4l-text">AI re-compose</div>
            <div className="flex flex-wrap gap-1.5">
              {[
                {
                  label: 'Mai formal',
                  instr: 'Rescrie textul cu un ton mai formal și profesional. Păstrează structura.',
                },
                {
                  label: 'Mai urgent',
                  instr: 'Subliniază urgența și deadline-urile. Tonalitate clară, directă.',
                },
                { label: 'Mai scurt', instr: 'Comprimă la jumătate, păstrează doar esențialul.' },
                { label: 'Reset', instr: '' },
              ].map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => recompose(preset.instr)}
                  disabled={composeMutation.isPending}
                  className="rounded border border-b4l-border bg-white px-2 py-1 text-xs hover:bg-b4l-bg disabled:opacity-50"
                >
                  {composeMutation.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    preset.label
                  )}
                </button>
              ))}
            </div>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                placeholder="Instrucțiune custom AI (ex: adaugă o frază despre buget)..."
                className="flex-1 rounded border border-b4l-border px-3 py-1.5 text-xs outline-none focus:border-b4l-green"
              />
              <Button
                variant="secondary"
                onClick={() =>
                  aiInstruction ? recompose(aiInstruction) : toast.error('Adaugă o instrucțiune')
                }
                disabled={composeMutation.isPending}
              >
                {composeMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wand2 className="h-3.5 w-3.5" />
                )}
                Aplică
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * Extrage mesaj util din axios error. Server-ul returneaza
 * { error: { code, message, fields: {camp1: ["msg1"], ...} } }
 */
function extractServerError(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback
  const e = err as {
    response?: {
      data?: {
        error?: {
          code?: string
          message?: string
          fields?: Record<string, string[]>
        }
      }
    }
    message?: string
  }
  const apiErr = e.response?.data?.error
  if (apiErr) {
    const parts: string[] = []
    if (apiErr.message) parts.push(apiErr.message)
    if (apiErr.fields) {
      const fieldMsgs = Object.entries(apiErr.fields)
        .map(([k, v]) => `${k}: ${v.join(', ')}`)
        .join(' · ')
      if (fieldMsgs) parts.push(fieldMsgs)
    }
    if (parts.length > 0) return parts.join(' — ')
  }
  return e.message ?? fallback
}
